import { useMemo, useState } from 'react';
import {
  Box,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { LocalFireDepartment, RemoveRedEye, Search, ThumbUp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../theme';
import { EmptyState, MainTableSkeleton, ScoreBar, fmt, fmtPct, getDisplayText, getDisplayTone, tdSx, thSx, trSx } from './shared';

export default function SignalsTableSection({ isFirstLoad, signals, onSelectTicker }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
  const [gradeFilter, setGradeFilter] = useState('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => signals.filter((signal) => {
    if (gradeFilter === 'watch') {
      if (signal.execution_grade !== 'C' && signal.execution_grade !== 'D') return false;
    } else if (gradeFilter !== 'all' && signal.execution_grade !== gradeFilter) {
      return false;
    }
    if (search && !signal.ticker.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [gradeFilter, search, signals]);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          {[
            { value: 'all',   label: t('vsa.filters.all'),     Icon: null,              color: colors.info.main },
            { value: 'A',    label: t('vsa.filters.best'),    Icon: LocalFireDepartment, color: colors.success.main, desc: t('vsa.filters.bestDesc') },
            { value: 'B',    label: t('vsa.filters.good'),    Icon: ThumbUp,             color: colors.info.main,    desc: t('vsa.filters.goodDesc') },
            { value: 'watch',label: t('vsa.filters.monitor'), Icon: RemoveRedEye,        color: colors.warning.main, desc: t('vsa.filters.monitorDesc') },
          ].map((option) => {
            const count = option.value === 'all' ? signals.length : option.value === 'watch' ? signals.filter((signal) => signal.execution_grade === 'C' || signal.execution_grade === 'D').length : signals.filter((signal) => signal.execution_grade === option.value).length;
            const isActive = gradeFilter === option.value;
            const button = (
              <Box key={option.value} onClick={() => setGradeFilter(option.value)} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.6, borderRadius: 2, cursor: 'pointer', border: `1px solid ${isActive ? alpha(option.color, 0.4) : '#e2e8f0'}`, bgcolor: isActive ? alpha(option.color, 0.08) : '#fff', transition: 'all 0.15s', '&:hover': { borderColor: alpha(option.color, 0.3), bgcolor: alpha(option.color, 0.04) } }}>
                {option.Icon && <option.Icon sx={{ fontSize: 13, color: isActive ? option.color : '#94a3b8' }} />}
                <Typography sx={{ fontSize: '0.72rem', fontWeight: isActive ? 800 : 600, color: isActive ? option.color : '#64748b' }}>{option.label}</Typography>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: isActive ? option.color : '#94a3b8', ml: 0.2 }}>{count}</Typography>
              </Box>
            );
            return option.desc ? <Tooltip key={option.value} title={option.desc} arrow placement="top">{button}</Tooltip> : button;
          })}
          <Box sx={{ width: '1px', height: 24, bgcolor: '#e2e8f0', mx: 0.5 }} />
          <TextField
            size="small"
            placeholder={t('vsa.filters.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
            sx={{ width: 180, '& .MuiOutlinedInput-root': { fontSize: '0.8rem', borderRadius: 2, '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: '#cbd5e1' }, '&.Mui-focused fieldset': { borderColor: '#3b82f6' } } }}
          />
        </Box>
          <Typography sx={{ fontSize: '0.72rem', color: colors.gray[400] }}><Box component="span" sx={{ fontWeight: 700, color: colors.gray[600] }}>{filtered.length}</Box> / {signals.length} {t('vsa.filters.signalCount')}</Typography>
      </Box>

      {isFirstLoad ? (
        <MainTableSkeleton />
      ) : filtered.length === 0 ? (
        <EmptyState Icon={Search} title={t('vsa.table.noSignals')} />
      ) : (
        <TableContainer sx={{ bgcolor: colors.background.paper, borderRadius: 1.5, border: `1px solid ${colors.gray[200]}`, overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thSx, position: 'sticky', left: 0, zIndex: 3, bgcolor: colors.gray[50] }}>{t('vsa.table.rank')}</TableCell>
                <TableCell sx={{ ...thSx, position: 'sticky', left: 30, zIndex: 3, bgcolor: colors.gray[50] }}>{t('vsa.table.ticker')}</TableCell>
                <TableCell sx={thSx}>{t('vsa.table.sector')}</TableCell>
                <TableCell sx={thSx}>{t('vsa.table.setup')}</TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'center' }}>{t('vsa.table.quality')}</TableCell>
                <TableCell sx={thSx}>{t('vsa.table.score')}</TableCell>
                <TableCell sx={thSx}>{t('vsa.table.entry')}</TableCell>
                <TableCell sx={thSx}>{t('vsa.table.stop')}</TableCell>
                <TableCell sx={thSx}>{t('vsa.table.risk')}</TableCell>
                <TableCell sx={thSx}>{t('vsa.table.target')}</TableCell>
                <TableCell sx={thSx}>{t('vsa.table.rr')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((signal, index) => {
                const tradePlan = signal.display?.trade_plan || {};
                const gradeTone = getDisplayTone(signal.display?.grade?.tone);
                const riskTone = getDisplayTone(tradePlan.risk_tone);
                const rrTone = getDisplayTone(tradePlan.rr_tone);
                const buyTip = signal.display?.buy_now ? getDisplayText(signal.display.buy_now, t, '') : '';
                return (
                  <TableRow key={index} hover sx={{ ...trSx(index, '#3b82f6'), bgcolor: signal.can_buy_now ? alpha('#10b981', 0.02) : undefined, cursor: 'pointer' }} onClick={() => onSelectTicker(signal.ticker)}>
                    <TableCell sx={{ ...tdSx, position: 'sticky', left: 0, bgcolor: colors.background.paper, zIndex: 1, fontWeight: 600, color: colors.gray[400], borderLeft: `3px solid ${signal.can_buy_now ? colors.success.main : 'transparent'}` }}>{index + 1}</TableCell>
                    <TableCell sx={{ ...tdSx, position: 'sticky', left: 30, bgcolor: colors.background.paper, zIndex: 1 }}>
                      <Tooltip title={buyTip} arrow placement="right">
                        <Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: colors.gray[900], letterSpacing: '0.02em', mb: 0.2 }}>{signal.ticker}</Typography>
                          <Typography sx={{ fontSize: '0.68rem', color: colors.gray[400], fontFamily: 'monospace' }}>{fmt(signal.close)}</Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.72rem', color: colors.gray[500] }}>{signal.sector_name?.[lang] || signal.sector || '—'}</Typography></TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.72rem', color: colors.gray[600] }}>{getDisplayText(signal.display?.setup, t, signal.setup_type)}</Typography></TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: 'center' }}><Tooltip title={getDisplayText({ key: signal.display?.grade?.tooltip_key }, t, '')} arrow placement="top"><Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: gradeTone.color }}>{signal.display?.grade?.text || signal.execution_grade}</Typography></Tooltip></TableCell>
                    <TableCell sx={tdSx}><ScoreBar score={signal.priority_score} /></TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace', color: colors.info.main }}>{fmt(tradePlan.entry_mid)}</Typography>
                      <Typography sx={{ fontSize: '0.62rem', color: colors.gray[400], fontFamily: 'monospace' }}>{fmt(signal.entry_zone_low)}-{fmt(signal.entry_zone_high)}</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace', color: colors.error.main }}>{fmt(signal.stop_loss)}</Typography></TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.75rem', fontWeight: 700, fontFamily: 'monospace', color: riskTone.color }}>{fmtPct(tradePlan.risk_pct, 1)}</Typography></TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace', color: colors.success.main }}>{fmt(tradePlan.target_value)}</Typography>
                      <Typography sx={{ fontSize: '0.62rem', color: colors.success.main, fontWeight: 600 }}>{fmtPct(tradePlan.target_pct, 1)}</Typography></TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.82rem', fontWeight: 800, fontFamily: 'monospace', color: rrTone.color }}>{tradePlan.rr_ratio != null ? `1:${tradePlan.rr_ratio.toFixed(1)}` : '—'}</Typography></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
